// The homepage's slider of up to three video ads (ad unit `homepage_slider`), in the A/B test's variation only.
// All ads come from one ad pod, so the ad server can keep their advertisers apart; each slide is titled with
// its advertiser's name from our own database, since the ad server does not carry it.

import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import getAdServerKeyValues from 'frontend/shared/javascripts/util/ad_server_key_values'
import { loadPlayer } from 'frontend/shared/javascripts/util/load_player'
import { AD_BANNER_CLIP } from 'frontend/shared/javascripts/video_player/ad_banner_clip'
import { getAdPodResponses, type AdsResponse } from 'player/javascripts/player/advertising'
import { fetchAdCompany, type AdCompany } from 'player/javascripts/player/ad_company'
import { track, trackInteraction } from 'frontend/shared/javascripts/tracking'
import type Player from 'player/javascripts/player'

const AD_UNIT = 'homepage_slider'
const MAX_SLIDES = 3
const LOAD_TIMEOUT = 2000
// Loading starts one viewport ahead, so the ads are there by the time the slider scrolls into view, and a
// slider that stays empty collapses before anyone sees it.
const LOAD_AHEAD_MARGIN = '100% 0px'
// Both observers watch the document rather than the implicit root, which inside a cross-origin frame ignores
// the margin and measures against the top-level viewport.
const AD_BUTTON_LABELS = { durationInfo: 'Jetzt mehr erfahren', visitAd: 'Chancen entdecken' }

export interface SliderAd {
  adsResponse: AdsResponse
  company: AdCompany
}

interface HomepageAdSliderData {
  advertisementUrlTemplate: string
}

interface Slide {
  element: HTMLElement
  player: Promise<Player>
  destroyPlayer: () => Promise<void>
  started: boolean
  playing: boolean
  finished: boolean
  inView: boolean
  viewTracked: boolean
}

type SlidePosition = 'active' | 'previous' | 'next' | 'hidden'

up.compiler(
  '[data-homepage-ad-slider]',
  (element: HTMLElement, { advertisementUrlTemplate }: HomepageAdSliderData) => {
    const carousel = element.querySelector<HTMLElement>('[data-homepage-ad-slider--carousel]')!
    const dotsContainer = element.querySelector<HTMLElement>('[data-homepage-ad-slider--dots]')!
    const slideTemplate = element.querySelector<HTMLTemplateElement>(
      '[data-homepage-ad-slider--slide-template]',
    )!
    const slides: Slide[] = []
    let activeSlide: Slide | null = null
    let variantActive = false
    let nearViewport = false
    let started = false
    let destroyed = false

    const loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          nearViewport = true
          loadObserver.disconnect()
          startWhenDue()
        }
      },
      { root: document, rootMargin: LOAD_AHEAD_MARGIN },
    )

    // Also what plays and pauses the ad in the middle: an ad nobody can see must not run, as its impression
    // would count all the same.
    const viewObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slide = slides.find((candidate) => candidate.element === entry.target)
          if (slide) {
            slide.inView = entry.intersectionRatio >= 0.5
            trackView(slide)
            if (slide === activeSlide) {
              if (slide.inView) {
                play(slide)
              } else {
                pause(slide)
              }
            }
          }
        }
      },
      { root: document, threshold: 0.5 },
    )

    function startWhenDue(): void {
      if (variantActive && nearViewport && !started) {
        started = true
        void start()
      }
    }

    async function start(): Promise<void> {
      const ads = await loadSliderAds(advertisementUrlTemplate)
      if (destroyed) {
        return
      }

      ads.forEach((ad) => slides.push(buildSlide(ad)))
      // tracking: homepage.ad_slider.fill
      track('ad_slider.filled', { ad_count: slides.length })
      if (slides.length === 0) {
        // tracking: player.ad_unfilled
        track('player.unfilledAdSlot', {
          keyValues: getAdServerKeyValues({}, AD_UNIT),
          playerType: 'banner',
          ad_unit: AD_UNIT,
        })
        element.classList.add('-empty')
      } else {
        activate(slides[0])
      }
    }

    function buildSlide(ad: SliderAd): Slide {
      const slideElement = slideTemplate.content.firstElementChild!.cloneNode(true) as HTMLElement
      slideElement.querySelector('[data-homepage-ad-slider--title]')!.textContent = ad.company.name

      const playerContainer = slideElement.querySelector<HTMLElement>('[data-player--container]')!
      const { playerPromise, destroyPlayer } = loadPlayer(playerContainer, {
        showAds: true,
        isVideoAdBanner: true,
        playerType: 'banner',
        adUnit: AD_UNIT,
        adsResponse: ad.adsResponse,
        adButtonLabels: AD_BUTTON_LABELS,
      })
      const slide: Slide = {
        element: slideElement,
        player: playerPromise.then((player) => {
          player.loadVideo(AD_BANNER_CLIP, {})
          return player
        }),
        destroyPlayer,
        started: false,
        playing: false,
        finished: false,
        inView: false,
        viewTracked: false,
      }

      // Side slides are no ads to click, only a way to bring them to the middle.
      up.on(slideElement, 'click', () => {
        if (slide !== activeSlide) {
          activate(slide)
        }
      })
      const preview = slideElement.querySelector<HTMLVideoElement>(
        '[data-homepage-ad-slider--preview]',
      )!
      const previewVideoUrl = ad.adsResponse.previewVideoUrl
      if (previewVideoUrl) {
        // Loaded past IMA, the still counts as no impression. Without a start time, a video that is not
        // playing shows no frame.
        preview.src = `${previewVideoUrl}#t=0.1`
      } else {
        preview.remove()
      }

      up.on(playerContainer, 'app:player:adStarted', () => {
        preview.remove()
        if (slide !== activeSlide || !slide.inView) {
          pause(slide, { force: true })
        }
      })
      up.on(playerContainer, 'app:player:adBannerFinished', () => onFinished(slide))
      up.on(playerContainer, 'app:player:noAdReceived app:player:mediaError', () => remove(slide))

      carousel.appendChild(slideElement)
      viewObserver.observe(slideElement)
      return slide
    }

    function activate(slide: Slide): void {
      if (activeSlide && activeSlide !== slide) {
        pause(activeSlide)
      }
      activeSlide = slide
      layout()
      trackView(slide)
      if (slide.inView) {
        play(slide)
      }
    }

    function play(slide: Slide): void {
      if (slide.playing || slide.finished) {
        return
      }
      const firstStart = !slide.started
      slide.started = true
      slide.playing = true
      void slide.player.then((player) => (firstStart ? player.playWhenReady() : player.resume()))
    }

    // `force` for an ad that started by itself, which the slider has not counted as playing.
    function pause(slide: Slide, { force = false }: { force?: boolean } = {}): void {
      if (!slide.playing && !force) {
        return
      }
      slide.playing = false
      void slide.player.then((player) => player.pause())
    }

    // Once per ad, and only in the middle: the blurred side slides are no impression.
    function trackView(slide: Slide): void {
      if (slide === activeSlide && slide.inView && !slide.viewTracked) {
        slide.viewTracked = true
        // tracking: homepage.ad_slider.view
        trackInteraction(
          'nativeBanner.visible',
          { id: 'ad_slider.view', text: 'Anzeige' },
          slide.element,
        )
      }
    }

    function step(direction: 1 | -1): void {
      const index = slides.indexOf(activeSlide!)
      activate(slides[(index + direction + slides.length) % slides.length])
    }

    // Autoplay: moves on to the next ad that has not run yet, and stops once every ad has run.
    function onFinished(slide: Slide): void {
      slide.finished = true
      slide.playing = false
      if (slide !== activeSlide) {
        return
      }

      const index = slides.indexOf(slide)
      const nextSlide = [...slides.slice(index + 1), ...slides.slice(0, index)].find(
        (candidate) => !candidate.finished,
      )
      if (nextSlide) {
        activate(nextSlide)
      }
    }

    // An ad that fails to play is dropped like one the ad server never sent.
    function remove(slide: Slide): void {
      const index = slides.indexOf(slide)
      if (index === -1) {
        return
      }

      slides.splice(index, 1)
      viewObserver.unobserve(slide.element)
      slide.element.remove()
      void slide.destroyPlayer()

      if (slides.length === 0) {
        activeSlide = null
        element.classList.add('-empty')
      } else if (slide === activeSlide) {
        activeSlide = null
        activate(slides[index % slides.length])
      } else {
        layout()
      }
    }

    function positionOf(slide: Slide): SlidePosition {
      const index = slides.indexOf(slide)
      const activeIndex = slides.indexOf(activeSlide!)
      if (index === activeIndex) {
        return 'active'
      } else if (slides.length < 3) {
        return index > activeIndex ? 'next' : 'previous'
      } else if (index === (activeIndex + 1) % slides.length) {
        return 'next'
      } else if (index === (activeIndex - 1 + slides.length) % slides.length) {
        return 'previous'
      } else {
        return 'hidden'
      }
    }

    function layout(): void {
      element.classList.toggle('-multiple', slides.length > 1)

      slides.forEach((slide, index) => {
        const position = positionOf(slide)
        slide.element.classList.remove('-active', '-previous', '-next', '-hidden')
        slide.element.classList.add(`-${position}`)
        slide.element.toggleAttribute('inert', position === 'hidden')
        // tracking: homepage.ad_slider.click — the player's ad clicks pick it up from here
        slide.element.dataset.trackingContext = JSON.stringify({ slide_position: index + 1 })
      })

      dotsContainer.replaceChildren(
        ...slides.map((slide, index) => {
          const dot = document.createElement('button')
          dot.type = 'button'
          dot.className = 'homepage-ad-slider--dot'
          dot.setAttribute('aria-label', `Werbung ${index + 1} von ${slides.length}`)
          dot.setAttribute('aria-current', String(slide === activeSlide))
          up.on(dot, 'click', () => activate(slide))
          return dot
        }),
      )
    }

    const destructors = [
      up.on(element.querySelector('[data-homepage-ad-slider--previous]')!, 'click', () => step(-1)),
      up.on(element.querySelector('[data-homepage-ad-slider--next]')!, 'click', () => step(1)),
      onSwipe(carousel, (direction) => {
        if (slides.length > 1) {
          step(direction)
        }
      }),
      // Not before the variation is decided: an ad requested for a visitor of the original would count as
      // an impression nobody saw.
      abTests.onVariant('homepage_ad_slider', (variant) => {
        variantActive = variant === 'b'
        startWhenDue()
      }),
    ]
    loadObserver.observe(element)

    return () => {
      destroyed = true
      loadObserver.disconnect()
      viewObserver.disconnect()
      destructors.forEach((destructor) => destructor())
      slides.forEach((slide) => void slide.destroyPlayer())
    }
  },
)

const SWIPE_DISTANCE = 50

// Touch only, as a mouse drag is no gesture anyone expects here. A swipe that ends on the ad must not
// count as a click on it, so its click is swallowed before it reaches the player.
function onSwipe(target: HTMLElement, callback: (direction: 1 | -1) => void): () => void {
  let start: { x: number; y: number } | null = null
  let swallowClick = false

  const destructors = [
    up.on(target, 'pointerdown', (event: PointerEvent) => {
      start = event.pointerType === 'mouse' ? null : { x: event.clientX, y: event.clientY }
    }),
    up.on(target, 'pointerup', (event: PointerEvent) => {
      if (!start) {
        return
      }
      const deltaX = event.clientX - start.x
      const deltaY = event.clientY - start.y
      start = null

      if (Math.abs(deltaX) > SWIPE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY)) {
        swallowClick = true
        setTimeout(() => (swallowClick = false), 0)
        callback(deltaX < 0 ? 1 : -1)
      }
    }),
    up.on(target, 'pointercancel', () => (start = null)),
  ]

  const swallow = (event: MouseEvent): void => {
    if (swallowClick) {
      up.event.halt(event)
    }
  }
  target.addEventListener('click', swallow, { capture: true })

  return () => {
    destructors.forEach((destructor) => destructor())
    target.removeEventListener('click', swallow, { capture: true })
  }
}

/**
 * The ads for the slides: at most `MAX_SLIDES`, in pod order, each with its advertiser. An ad whose
 * advertiser cannot be resolved is left out as if the ad server had not sent it, and so is everything that
 * misses `LOAD_TIMEOUT`.
 */
export async function loadSliderAds(advertisementUrlTemplate: string): Promise<SliderAd[]> {
  const deadline = new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_TIMEOUT))
  const keyValues = getAdServerKeyValues({}, AD_UNIT) as unknown as Record<string, string>

  let adsResponses: AdsResponse[] | null
  try {
    adsResponses = await Promise.race([getAdPodResponses(keyValues, '', AD_UNIT), deadline])
  } catch {
    // Probably an ad blocker
    return []
  }

  const ads = await Promise.all(
    (adsResponses ?? []).map(async (adsResponse): Promise<SliderAd | null> => {
      const { companyIdentifier } = adsResponse
      const company = companyIdentifier
        ? await Promise.race([
            fetchAdCompany(companyIdentifier, advertisementUrlTemplate),
            deadline,
          ])
        : null

      return company ? { adsResponse, company } : null
    }),
  )

  return ads.filter((ad) => ad !== null).slice(0, MAX_SLIDES)
}
